import * as React from "react";
import {
  TextField,
  EmailField,
  Show,
  ReferenceField,
  BooleanField,
  usePermissions,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  Button,
  EditButton,
  TopToolbar,
  LinearProgress,
  PrevNextButtons,
  DateField,
} from "react-admin";
import { ROLE_TYPES } from "../enums/Roles";
import { SimpleShowLayout } from "../common/SimpleShowLayout";
import { AuditButton } from "../common/AuditButton";
import { DeactivationNoteDialog } from "../common/DeactivationNoteDialog";
import IconDeactivate from "@mui/icons-material/Clear";
import { CheckCircle } from "@mui/icons-material";

const UserShowActions = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const record = useRecordContext();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleDeactivateUser = (note) => {
    notify("Desactivando usuario...");
    setIsLoading(true);
    dataProvider
      .deactivateUser("users", { id: record.id, note })
      .then(() => {
        notify("Usuario desactivado con éxito", {
          type: "success",
        });
        setDialogOpen(false);
        refresh();
      })
      .catch(() => {
        notify("Ha ocurrido un error desactivando el usuario", {
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConfirmUser = () => {
    notify("Confirmando usuario...");
    setIsLoading(true);
    dataProvider
      .confirmUser("users", { id: record.id })
      .then(() => {
        notify("Usuario confirmado con éxito", {
          type: "success",
        });
        refresh();
      })
      .catch(() => {
        notify("Ha ocurrido un error confirmando el usuario", {
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <TopToolbar>
      {isLoading && <LinearProgress />}
      {!isLoading && (
        <>
          <EditButton />
          {permissions === ROLE_TYPES.ADMIN && record?.active && (
            <>
              <Button
                label="Desactivar"
                onClick={() => setDialogOpen(true)}
                disabled={!record?.active}
              >
                <IconDeactivate />
              </Button>

              {!record?.confirmed && (
                <Button onClick={handleConfirmUser} label="Confirmar">
                  <CheckCircle />
                </Button>
              )}
            </>
          )}
          {permissions === ROLE_TYPES.ADMIN && <AuditButton />}
        </>
      )}
      <DeactivationNoteDialog
        isOpen={dialogOpen}
        initialNote={record?.note ?? ""}
        loading={isLoading}
        onConfirm={handleDeactivateUser}
        onClose={() => setDialogOpen(false)}
      />
      {!isLoading && <PrevNextButtons linkType="show" />}
    </TopToolbar>
  );
};

const UserTitle = () => {
  const record = useRecordContext();
  return (
    <span>
      Usuario {record ? `"${record.firstName} ${record.lastName}"` : ""}
    </span>
  );
};

const UserShow = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  return (
    <SimpleShowLayout>
      {permissions === ROLE_TYPES.ADMIN && <TextField source="id" label="ID" />}
      <TextField source="firstName" label="Nombre" />
      <TextField source="lastName" label="Apellido" />
      <EmailField source="email" label="Email" />
      <TextField source="type" label="Tipo" />
      <TextField source="phone" label="Teléfono" />
      {permissions === ROLE_TYPES.ADMIN && (
        <ReferenceField
          source="restaurant"
          reference="restaurants"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
      )}
      <BooleanField source="active" label="Activo" />
      {record?.deactivatedAt && (
        <DateField
          source="deactivatedAt"
          label="Fecha de desactivación"
          showTime
        />
      )}
      <BooleanField source="confirmed" label="Confirmado" />
      <BooleanField source="profile.verified" label="Verificado" />
      {permissions === ROLE_TYPES.ADMIN && (
        <TextField source="note" label="Nota" emptyText="-" />
      )}
    </SimpleShowLayout>
  );
};

const UserShowContainer = () => (
  <Show title={<UserTitle />} actions={<UserShowActions />}>
    <UserShow />
  </Show>
);

export default UserShowContainer;
